import { Id, Identifiable, List, Task } from "@planzone/dto";
import { events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { resizeList } from "./utils/resizeList";
import { reduceTask } from "../task";
import { reduceMilestone } from "../milestone";

function editTasks(
  list: Identifiable<List>,
  task: Boundaries["editTask"]["success"],
) {
  return list.tasks.reduce<{
    hasMatch: boolean;
    tasks: Identifiable<Task>[];
  }>(
    ({ hasMatch, tasks }, t) => ({
      hasMatch: hasMatch || t.uuid === task.uuid,
      tasks: [
        ...tasks,
        reduceTask(t, { type: events.editTaskSucceeded, data: task }),
      ],
    }),
    { hasMatch: false, tasks: [] },
  );
}

function editSubLists(
  list: Identifiable<List>,
  task: Boundaries["editTask"]["success"],
  impactedLists: {
    uuid: Id;
    endDate: string;
    startDate: string;
    minStartDate?: string;
    maxStartDate?: string;
    minEndDate?: string;
    maxEndDate?: string;
  }[],
) {
  return list.subLists.reduce<{
    hasMatch: boolean;
    subLists: Identifiable<List>[];
  }>(
    ({ hasMatch, subLists }, subList) => {
      const { hasMatch: hasSubListMatch, list: nextSubList } = editList(
        subList,
        task,
        impactedLists,
      );

      return {
        hasMatch: hasMatch || hasSubListMatch,
        subLists: [...subLists, nextSubList],
      };
    },
    { hasMatch: false, subLists: [] },
  );
}

function editList(
  list: Identifiable<List>,
  task: Boundaries["editTask"]["success"],
  impactedLists: {
    uuid: Id;
    endDate: string;
    startDate: string;
    minStartDate?: string;
    maxStartDate?: string;
    minEndDate?: string;
    maxEndDate?: string;
  }[],
): { hasMatch: boolean; list: Identifiable<List> } {
  const { hasMatch, tasks } = editTasks(list, task);
  const impactedList = impactedLists.find(({ uuid }) => uuid === list.uuid);
  const { hasMatch: hasSubListMatch, subLists } = editSubLists(
    list,
    task,
    impactedLists,
  );
  const nextList = {
    ...list,
    ...(impactedList
      ? {
          startDate: impactedList.startDate,
          endDate: impactedList.endDate,
          ...(impactedList.minStartDate !== undefined && {
            minStartDate: impactedList.minStartDate,
          }),
          ...(impactedList.maxStartDate !== undefined && {
            maxStartDate: impactedList.maxStartDate,
          }),
          ...(impactedList.minEndDate !== undefined && {
            minEndDate: impactedList.minEndDate,
          }),
          ...(impactedList.maxEndDate !== undefined && {
            maxEndDate: impactedList.maxEndDate,
          }),
        }
      : {}),
    tasks,
    milestones: list.milestones.map((milestone) =>
      reduceMilestone(milestone, {
        type: events.editTaskSucceeded,
        data: task,
      }),
    ),
    subLists,
  };
  const isMatching = hasMatch || hasSubListMatch;

  return {
    hasMatch: isMatching,
    list: isMatching ? resizeList(nextList, task.endDate) : nextList,
  };
}

function handleEditTask(
  list: Identifiable<List>,
  task: Boundaries["editTask"]["success"],
): Identifiable<List> {
  const impactedLists = task.impactedElements
    ? task.impactedElements.filter(({ type }) => type === "list")
    : [];

  return editList(list, task, impactedLists).list;
}

export { handleEditTask };
